import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, ChevronLeft, ChevronRight, Info, Loader2 } from 'lucide-react';
import { useSidebar } from '../context/SidebarContext';
import { supabase } from '../lib/supabase';
import { GENERATE_AUTH_TOKEN } from '../config';
import LoadingAnimation from '../components/LoadingAnimation';

const accentOptions = [
  { value: 'argentino', label: 'Argentino' },
  { value: 'chileno', label: 'Chileno' },
  { value: 'mexicano', label: 'Mexicano' },
  { value: 'colombiano', label: 'Colombiano' },
  { value: 'peruano', label: 'Peruano' },
  { value: 'venezolano', label: 'Venezolano' },
  { value: 'ecuatoriano', label: 'Ecuatoriano' },
  { value: 'boliviano', label: 'Boliviano' },
  { value: 'uruguayo', label: 'Uruguayo' },
  { value: 'paraguayo', label: 'Paraguayo' },
  { value: 'español', label: 'Español' },
  { value: 'cubano', label: 'Cubano' },
  { value: 'dominicano', label: 'Dominicano' },
  { value: 'puertorriqueno', label: 'Puertorriqueño' },
  { value: 'costarricense', label: 'Costarricense' },
  { value: 'panameño', label: 'Panameño' },
  { value: 'guatemalteco', label: 'Guatemalteco' },
  { value: 'hondureño', label: 'Hondureño' },
  { value: 'salvadoreño', label: 'Salvadoreño' },
  { value: 'nicaraguense', label: 'Nicaragüense' },
  { value: 'ingles', label: 'Inglés' },
];

const videoStyleLabels: Record<string, string> = {
  vlog: 'Vlog',
  visitando_negocio: 'Visitando tu negocio',
  unboxing: 'Unboxing',
  automovil: 'Automóvil',
  podcast: 'Podcast',
  exterior: 'Exterior',
  entrevista: 'Entrevista',
  comparativa: 'Comparativa',
};

const ConfigurarVideoInfluencerVideo: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const idsParam = params.get('ids') || '';
  const ids = useMemo(
    () => idsParam.split(',').map(s => s.trim()).filter(Boolean).filter(s => s !== '0'),
    [idsParam],
  );
  const styleId = params.get('style') || 'vlog';
  const styleLabel = videoStyleLabels[styleId] || styleId;
  const imageIdFromParams = params.get('imageId') || '';
  const companyId = params.get('companyId') || '';

  // Allow overriding imageId when returning from editor
  const locationState = location.state as { editedImageId?: string } | null;
  const imageId = locationState?.editedImageId || imageIdFromParams;

  const { sidebarWidth, setMobileTitleOverride, setMobileBackOverride } = useSidebar();

  // Generated image URL
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(true);

  // Accent & dialogue
  const [selectedAccent, setSelectedAccent] = useState<string>('argentino');
  const [accentDropdownOpen, setAccentDropdownOpen] = useState(false);
  const accentDropdownRef = useRef<HTMLDivElement>(null);
  const [dialogue, setDialogue] = useState<string>('');
  const [loadingDialogue, setLoadingDialogue] = useState<boolean>(true);
  const [dialogueError, setDialogueError] = useState<string | null>(null);
  const [duration, setDuration] = useState<number>(6);
  const [mobileImageExpanded, setMobileImageExpanded] = useState(false);

  // Generate video state
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [completeProgressNow, setCompleteProgressNow] = useState(false);
  const willNavigateRef = useRef(false);
  const pendingNavRef = useRef<{ videoId: string; companyId: string } | null>(null);

  // Insufficient images modal state
  const [showInsufficientImagesModal, setShowInsufficientImagesModal] = useState(false);
  const [requiredImagesCount, setRequiredImagesCount] = useState(24);
  const [remainingImagesCount, setRemainingImagesCount] = useState(0);
  const [nextRenewalDate, setNextRenewalDate] = useState<Date | null>(null);

  // Close accent dropdown on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (accentDropdownRef.current && !accentDropdownRef.current.contains(e.target as Node)) {
        setAccentDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Fetch the generated image signed URL — polls until file_name is available
  useEffect(() => {
    if (!imageId || !companyId) { setImageLoading(false); return; }
    let cancelled = false;

    const fetchImage = async () => {
      const maxAttempts = 10;
      const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        if (cancelled) return;
        try {
          const { data: imgRow, error: imgError } = await supabase
            .from('imagenes_generadas')
            .select('file_name')
            .eq('id', imageId)
            .eq('id_company', companyId)
            .single();

          if (imgError || !imgRow?.file_name) {
            console.log(`[ConfigurarVideoInfluencerVideo] Attempt ${attempt}: no file_name yet for ${imageId}`);
            if (attempt < maxAttempts) { await delay(1500); continue; }
            break;
          }

          const filePath = `${companyId}/full/${imgRow.file_name}`;
          const { data: signed } = await supabase.storage
            .from('generadas')
            .createSignedUrl(filePath, 3600);

          if (signed?.signedUrl && !cancelled) {
            setImageUrl(signed.signedUrl);
          }
          break;
        } catch (err) {
          console.error(`[ConfigurarVideoInfluencerVideo] Attempt ${attempt} error:`, err);
          if (attempt < maxAttempts) { await delay(1500); continue; }
        }
      }
      if (!cancelled) setImageLoading(false);
    };

    fetchImage();
    return () => { cancelled = true; };
  }, [imageId, companyId]);

  // Auto-generate dialogue on mount (guarded to prevent double-fire in Strict Mode)
  const dialogueFetchedRef = useRef(false);
  useEffect(() => {
    if (dialogueFetchedRef.current) return;
    dialogueFetchedRef.current = true;

    const fetchDialogue = async () => {
      try {
        setLoadingDialogue(true);
        setDialogueError(null);

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('No se encontró el usuario');

        const variantId = ids[0] || '';
        if (!variantId) {
          setDialogue('');
          setLoadingDialogue(false);
          return;
        }

        const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/generar_dialogo', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': GENERATE_AUTH_TOKEN,
          },
          body: JSON.stringify({
            user_id: user.id,
            variante_id: variantId,
            tipo_de_video: styleId,
          }),
        });

        if (!response.ok) throw new Error('Error al generar el diálogo');

        const data = await response.json();
        setDialogue(data?.response || '');
      } catch (e) {
        console.error('Error fetching dialogue:', e);
        setDialogueError(e instanceof Error ? e.message : 'Error al generar el diálogo');
      } finally {
        setLoadingDialogue(false);
      }
    };

    fetchDialogue();
  }, [ids]);

  // Auto-set duration to recommended value based on dialogue length
  useEffect(() => {
    const trimmed = dialogue.trim();
    if (!trimmed) return;
    const wordCount = trimmed.split(/\s+/).length;
    const recommended = Math.max(6, Math.ceil(wordCount / 3.5));
    setDuration(Math.min(15, Math.max(3, recommended)));
  }, [dialogue]);

  // Mobile header
  useEffect(() => {
    setMobileTitleOverride(' ');
    return () => setMobileTitleOverride(null);
  }, [setMobileTitleOverride]);

  useEffect(() => {
    setMobileBackOverride(() => handleBack);
    return () => setMobileBackOverride(null);
  }, [setMobileBackOverride]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBack = () => {
    navigate(-1);
  };

  const handleEditImage = () => {
    if (!imageId || !companyId) return;
    // Build the return URL so EditarImagen can navigate back here
    const returnUrl = location.pathname + location.search;
    navigate(`/redes-sociales/editar-imagen?id=${imageId}&companyId=${companyId}`, {
      state: {
        returnToVideo: true,
        returnUrl,
        videoPageParams: { ids: idsParam, style: styleId, companyId },
      },
    });
  };

  const handleGenerateVideo = async () => {
    if (generating) return;
    setGenerating(true);
    setGenError(null);
    setShowLoadingScreen(true);
    setCompleteProgressNow(false);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');

      // Check image quota before generating video
      const requiredImages = duration;
      if (companyId) {
        const { data: companyProfile } = await supabase
          .from('company_profiles')
          .select('plan, imagenes_restantes, max_fotos_mes, paid_plan_start_date, upgrade_plan_date')
          .eq('id_company', companyId)
          .single();

        const remaining = (companyProfile as any)?.imagenes_restantes as number | null | undefined;
        if (typeof remaining === 'number' && remaining < requiredImages) {
          setRequiredImagesCount(requiredImages);
          setRemainingImagesCount(remaining);
          const paidStart = (companyProfile as any)?.paid_plan_start_date as string | null;
          const upgradeDate = (companyProfile as any)?.upgrade_plan_date as string | null;
          if (paidStart) {
            const now = new Date();
            const ref = upgradeDate ? new Date(upgradeDate) : new Date(paidStart);
            const anchorDay = ref.getDate();
            const daysInMonth = (y: number, m: number) => new Date(y, m + 1, 0).getDate();
            const anchorFor = (y: number, m: number) => new Date(y, m, Math.min(anchorDay, daysInMonth(y, m)));
            let start = anchorFor(now.getFullYear(), now.getMonth());
            if (start.getTime() > now.getTime()) {
              const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
              start = anchorFor(prev.getFullYear(), prev.getMonth());
            }
            const next = new Date(start.getFullYear(), start.getMonth() + 1, 1);
            setNextRenewalDate(anchorFor(next.getFullYear(), next.getMonth()));
          } else {
            setNextRenewalDate(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1));
          }
          setShowInsufficientImagesModal(true);
          setGenerating(false);
          setShowLoadingScreen(false);
          return;
        }
      }

      const requestData: Record<string, string> = {
        user_id: user.id,
        tipo_de_video: styleId,
        dialogo: dialogue.trim(),
        acento: selectedAccent,
        imagen_id: imageId,
      };
      requestData.duracion = String(duration);

      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/start_video_ugc', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `Error ${response.status}`);
      }

      const data = await response.json();
      if (data?.response === 'success' && data?.video_generado_id) {
        const videoId = String(data.video_generado_id);
        willNavigateRef.current = true;
        pendingNavRef.current = { videoId, companyId };
        setCompleteProgressNow(true);
      } else {
        throw new Error('No se recibió el video generado.');
      }
    } catch (e: any) {
      console.error('[ConfigurarVideoInfluencerVideo] handleGenerateVideo error', e);
      setGenError('Se produjo un error. No se consumieron imágenes. Por favor, volvé a intentarlo.');
      setGenerating(false);
      setShowLoadingScreen(false);
    }
  };

  // Image content
  const cardImageContent = imageLoading ? (
    <div className="w-full h-full flex items-center justify-center bg-neutral-100">
      <Loader2 className="w-8 h-8 text-[#1644EB] animate-spin" />
    </div>
  ) : imageUrl ? (
    <img
      src={imageUrl}
      alt={styleLabel}
      className="w-full rounded-lg"
    />
  ) : (
    <div className="w-full h-full flex items-center justify-center bg-neutral-100">
      <span className="material-symbols-rounded text-[40px] text-brand-black/20">image</span>
    </div>
  );

  // Options form — Accent + Dialogue
  const optionsForm = (
    <div>
      {/* Diálogo */}
      <div>
        <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">Diálogo</p>

        {/* Accent row — custom dropdown */}
        <div ref={accentDropdownRef} className="relative z-10">
          <div className="rounded-[13px] bg-white shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
            <button
              type="button"
              onClick={() => setAccentDropdownOpen(!accentDropdownOpen)}
              className="w-full px-4 py-[11px] flex items-center justify-between rounded-[13px]"
            >
              <span className="flex items-center gap-3">
                <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">language</span>
                <span className="text-[15px] text-brand-black/90">Acento</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="text-[15px] text-[#3C3C43]/60">{accentOptions.find(o => o.value === selectedAccent)?.label}</span>
                <ChevronRight className="w-[14px] h-[14px] text-[#C7C7CC]" />
              </span>
            </button>
          </div>
          {accentDropdownOpen && (
            <div className="absolute z-50 mt-1 right-0 min-w-[180px] max-h-60 overflow-y-auto bg-white border border-neutral-200 rounded-md shadow-md py-1">
              {accentOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => { setSelectedAccent(option.value); setAccentDropdownOpen(false); }}
                  className={`w-full text-left px-4 py-2 text-[15px] flex items-center justify-between transition-colors ${
                    selectedAccent === option.value ? 'bg-neutral-100 text-brand-black/90 font-medium' : 'text-brand-black/70 hover:bg-neutral-100'
                  }`}
                >
                  <span>{option.label}</span>
                  {selectedAccent === option.value && <Check className="w-[18px] h-[18px] text-[#1644EB] shrink-0" />}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Dialogue content — iOS card */}
        <div className="mt-3">
          {loadingDialogue ? (
            <div className="bg-white rounded-[13px] p-4 shadow-[0_0_0_0.5px_rgba(0,0,0,0.04),0_2px_8px_rgba(0,0,0,0.04)]">
              <div className="flex items-center gap-2 mb-3">
                <Loader2 className="w-5 h-5 text-[#1644EB] animate-spin" />
                <span className="text-[15px] text-[#3C3C43]/60">Generando diálogo...</span>
              </div>
              <div className="w-full h-1 bg-black/[0.06] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#1644EB] rounded-full"
                  style={{ animation: 'dialogueProgress 15s linear forwards' }}
                />
              </div>
              <style>{`
                @keyframes dialogueProgress {
                  from { width: 0%; }
                  to { width: 100%; }
                }
              `}</style>
            </div>
          ) : dialogueError ? (
            <div className="bg-white rounded-[13px] p-4 shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
              <p className="text-[15px] text-[#FF3B30]">{dialogueError}</p>
            </div>
          ) : (
            <div className="bg-white rounded-[13px] p-4 shadow-[0_0_0_0.5px_rgba(0,0,0,0.04),0_2px_8px_rgba(0,0,0,0.04)]">
              <p className="text-[13px] text-[#3C3C43]/60 mb-2">Este es el texto que dirá el influencer en el video.</p>
              <textarea
                rows={6}
                value={dialogue}
                onChange={(e) => setDialogue(e.target.value)}
                placeholder="El diálogo aparecerá aquí..."
                className="w-full rounded-[10px] border-none bg-black/[0.04] p-3 text-[15px] text-brand-black/90 placeholder:text-brand-black/25 focus:outline-none focus:ring-0 resize-none"
              />
              {(() => {
                const trimmed = dialogue.trim();
                if (!trimmed) {
                  return (
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-black/[0.06]">
                      <Info className="w-4 h-4 text-[#1644EB] shrink-0" />
                      <span className="text-[13px] text-[#3C3C43]/60">
                        Por favor ingresá el diálogo que dirá el influencer en el video.
                      </span>
                    </div>
                  );
                }
                const wordCount = trimmed.split(/\s+/).length;
                const recommendedSeconds = Math.max(6, Math.ceil(wordCount / 3.5));
                return (
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-black/[0.06]">
                    <Info className="w-4 h-4 text-[#1644EB] shrink-0" />
                    <span className="text-[13px] text-[#3C3C43]/60">
                      Para este diálogo se recomienda una duración de video de {recommendedSeconds} segundos.
                    </span>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Duración */}
      <div className="mt-6">
        <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2 px-4">Duración</p>
        <div className="rounded-[13px] bg-white overflow-hidden shadow-[0_0_0_0.5px_rgba(0,0,0,0.04)]">
          <div className="px-4 py-[11px]">
            <div className="flex items-center gap-3 mb-3">
              <span className="material-symbols-rounded text-[#1644EB] text-[20px] shrink-0">timer</span>
              <span className="text-[15px] text-brand-black/90">Duración del video</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex-1 flex flex-col">
                <div className="relative h-[22px] flex items-center">
                  <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[6px] rounded-full bg-neutral-200" />
                  <div className="absolute top-1/2 -translate-y-1/2 left-0 h-[6px] rounded-full bg-[#1644EB]" style={{ width: `${((duration - 4) / 11) * 100}%` }} />
                  <input
                    type="range"
                    min={4}
                    max={15}
                    step={1}
                    value={duration}
                    onChange={(e) => setDuration(Number(e.target.value))}
                    className="absolute inset-0 w-full appearance-none bg-transparent cursor-pointer z-10 m-0 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-[22px] [&::-webkit-slider-thumb]:h-[22px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-[0_0.5px_4px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.04)] [&::-webkit-slider-thumb]:border-0 [&::-webkit-slider-runnable-track]:bg-transparent [&::-webkit-slider-runnable-track]:h-[22px]"
                  />
                </div>
                <div className="flex justify-between px-[2px] mt-1">
                  {Array.from({ length: 12 }, (_, i) => (
                    <div key={i} className={`w-[3px] h-[3px] rounded-full ${i + 4 <= duration ? 'bg-[#1644EB]/40' : 'bg-neutral-300'}`} />
                  ))}
                </div>
              </div>
              <span className="text-[15px] font-medium text-brand-black/90 tabular-nums shrink-0 min-w-[58px] text-right">{duration} seg</span>
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 px-4 mt-3 mb-6 sm:mb-0">
        <Info className="w-3.5 h-3.5 text-[#3C3C43]/40 shrink-0" />
        <p className="text-[13px] text-[#3C3C43]/60">Se consumirán {duration} imágenes.</p>
      </div>
    </div>
  );

  return (
    <div className="bg-[#EFF0F5]">
      {/* Desktop floating back button */}
      <div className="hidden sm:block fixed top-0 right-0 z-40" style={{ left: sidebarWidth }}>
        <div className="max-w-6xl mx-auto px-8 h-14 flex items-center">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex items-center gap-1 text-[#1644EB] hover:opacity-70 transition-opacity text-[15px] font-normal"
          >
            <ChevronLeft className="w-5 h-5" />
            <span>Atrás</span>
          </button>
        </div>
      </div>

      {/* Desktop layout: image left, options right */}
      <div className="hidden sm:flex max-w-6xl mx-auto px-8 pt-14 gap-6">
        <div className="w-[400px] shrink-0 sticky top-[80px] self-start">
          <p className="text-[17px] font-semibold text-brand-black/90 mb-3">{styleLabel}</p>
          <div>
            {cardImageContent}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          {optionsForm}
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={handleEditImage}
              className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-[10px] text-[15px] font-medium border border-neutral-200 bg-white text-brand-black/90 hover:bg-neutral-50 transition-all"
            >
              <span className="material-symbols-rounded text-[18px]">edit</span>
              Editar imagen
            </button>
            <button
              type="button"
              onClick={handleGenerateVideo}
              disabled={generating || loadingDialogue}
              className={`inline-flex items-center justify-center gap-2 h-11 px-8 rounded-[10px] text-[15px] font-medium transition-all ${
                (generating || loadingDialogue)
                  ? 'bg-[#1644EB]/40 text-white/60 cursor-not-allowed'
                  : 'bg-[#1644EB] hover:bg-[#1238C8] active:bg-[#0F2FA8] text-white'
              }`}
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generando...
                </>
              ) : 'Generar video'}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile layout: image top, options below */}
      <div className="sm:hidden">
        <div className="px-4 pt-4 flex justify-center">
          <div
            className="cursor-pointer"
            style={{
              width: mobileImageExpanded ? '80%' : 'calc(50% - 4px)',
              transition: 'width 0.35s cubic-bezier(0.25, 0.1, 0.25, 1)',
            }}
            onClick={() => setMobileImageExpanded(prev => !prev)}
          >
            {cardImageContent}
          </div>
        </div>
        <p className="text-[17px] font-semibold text-brand-black/90 text-center mt-2 px-4">{styleLabel}</p>

        {/* Editar imagen button — mobile */}
        <div className="px-4 mt-3">
          <button
            type="button"
            onClick={handleEditImage}
            className="w-full h-10 rounded-[10px] text-[15px] font-medium border border-neutral-200 bg-white text-brand-black/90 active:bg-neutral-50 transition-colors inline-flex items-center justify-center gap-2"
          >
            <span className="material-symbols-rounded text-[18px]">edit</span>
            Editar imagen
          </button>
        </div>

        {/* Options */}
        <div className="px-4 mt-3 mb-4">
          {optionsForm}
        </div>
      </div>

      {/* Mobile bottom bar */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white/72 backdrop-blur-xl border-t border-black/[0.06] px-4 py-3 z-30" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}>
        {loadingDialogue ? (
          <div className="flex items-center justify-center gap-2 text-[13px] text-[#3C3C43]/60 mb-2">
            <Loader2 className="w-3.5 h-3.5 text-[#1644EB] animate-spin" />
            <span>Generando diálogo...</span>
          </div>
        ) : null}
        <button
          type="button"
          onClick={handleGenerateVideo}
          disabled={generating || loadingDialogue}
          className={`w-full h-[50px] rounded-[13px] text-[17px] font-semibold transition-colors ${
            (generating || loadingDialogue)
              ? 'bg-black/[0.06] text-[#3C3C43]/30 cursor-not-allowed'
              : 'bg-[#1644EB] active:bg-[#1238C8] text-white shadow-[0_2px_8px_rgba(22,68,235,0.25)]'
          }`}
        >
          {generating ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              Generando...
            </span>
          ) : 'Generar video'}
        </button>
      </div>

      {/* Loading Animation */}
      {showLoadingScreen && (
        <LoadingAnimation
          message="Generando video..."
          subMessage="Esto puede tardar unos segundos."
          durationMs={35000}
          completeNow={completeProgressNow}
          onCompleted={() => {
            setShowLoadingScreen(false);
            setGenerating(false);
            if (willNavigateRef.current && pendingNavRef.current) {
              willNavigateRef.current = false;
              const { videoId, companyId: cId } = pendingNavRef.current;
              pendingNavRef.current = null;
              navigate('/redes-sociales/imagenes-generadas', {
                state: { imageIds: [videoId], companyId: cId },
              });
            }
          }}
        />
      )}

      {/* Insufficient Images Modal */}
      {showInsufficientImagesModal && (
        <div className="fixed inset-0 bg-brand-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center">
          <div className="bg-surface-card rounded-lg shadow-md w-full max-w-xl mx-4">
            <div className="p-5">
              <h3 className="text-[17px] font-semibold text-brand-black/90 mb-3">
                Imágenes insuficientes para generar video
              </h3>
              <p className="text-[15px] text-brand-black/90 mb-2">
                Para generar este video necesitás <strong>{requiredImagesCount} imágenes</strong>, pero {remainingImagesCount === 0 ? <>no te quedan imágenes restantes este mes</> : <>solo tenés <strong>{remainingImagesCount}</strong> restantes este mes</>}.
              </p>
              {nextRenewalDate && (
                <p className="text-[15px] text-neutral-500 mb-2">
                  Tu cupo de imágenes se renovará el {nextRenewalDate.toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' })}.
                </p>
              )}
              <p className="text-[15px] text-brand-black/90 mb-6">
                Para generar videos ya mismo, podés mejorar tu plan.
              </p>
              <div className="flex flex-col sm:flex-row gap-2.5 items-center justify-center sm:justify-end">
                <button
                  type="button"
                  onClick={() => { setShowInsufficientImagesModal(false); navigate('/subscription'); }}
                  className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
                >
                  Mejorar mi plan
                </button>
                <button
                  type="button"
                  onClick={() => setShowInsufficientImagesModal(false)}
                  className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto border border-neutral-200 bg-surface-card text-brand-black/90 text-[15px] font-medium rounded-md shadow-sm hover:bg-neutral-100 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error toast */}
      {genError && (
        <div className="fixed bottom-24 sm:bottom-8 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] p-4 z-50">
          <div className="flex items-start gap-3">
            <span className="material-symbols-rounded text-[22px] text-[#FF3B30] shrink-0">error</span>
            <div className="flex-1 min-w-0">
              <p className="text-[15px] font-semibold text-brand-black/90">Error al generar</p>
              <p className="text-[13px] text-[#3C3C43]/60 mt-0.5">{genError}</p>
            </div>
            <button type="button" onClick={() => setGenError(null)} className="text-[#3C3C43]/30 hover:text-[#3C3C43]/60 transition-colors shrink-0 -mt-0.5">
              <span className="material-symbols-rounded text-[18px]">close</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfigurarVideoInfluencerVideo;
